import { Box, Button, Flex, Heading, Image, Text } from '@chakra-ui/react';
import CheckBoxIcon from 'components/Icons/ADMIN/CheckBoxIcon';
import FilledCheckBoxIcon from 'components/Icons/ADMIN/FilledCheckBoxIcon';
import type { CSSProperties, FC} from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from 'shared/utils/chakra-theme/colors';
import { notificationDateFormatter } from 'utils/helpers/date/dateFormatter';
import type { AdminFullProps, AdminNotifToastProps, ShortPropsAdmin } from '../../../../../utils/types/notifications/templates';
import { useNavigate } from 'react-router-dom';
import { OneColorIcon } from '../../Story/Icon';
import { REQUESTS_TABS } from 'utils/enums/admin-tabs.enum';
import { ToastWrapper } from '../../UI/Wrapper/ToastWrapper';
import { BodyWithBookAndAuthor } from '../../UI/Toast/BodyWithBookAndAuthor';

export const NewBookReviewRequestToast: FC<Required<AdminNotifToastProps>> = ({
  id,
  title,
  handleClose,
  createdBy
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleReadFull = () => {
    navigate(`/admin/inbox/${id}`);
  };

  return (
    <ToastWrapper
      handleClose={() => handleClose(id)}
    >
      <Box mr="12px">
        <OneColorIcon
          color="linear-gradient(180deg, #B86BF8 0%, #7F5DD9 100%)"
          img="/images/notifications/admin/book.svg"
        />
      </Box>

      <BodyWithBookAndAuthor
        title={t('Book Review Request')}
        textA={t('For ')}
        textB={t('by ')}
        bookTitle={title}
        createdBy={createdBy}
        handleReadFull={handleReadFull}
      />
    </ToastWrapper>
  );
};

export const NewBookReviewRequestShort: FC<ShortPropsAdmin> = ({
  id,
  isSelected,
  user_readed,
  title,
  createdBy,
  createdAt,
  toggleSelect,
  handleOpenNotification,
}) => {
  const { t } = useTranslation();
  const [bg, border] = useMemo(() => {
    if (isSelected) {
      return ['rgba(229, 243, 255, 0.25)', colors['primary-blue-100']];
    }

    return user_readed ? [colors.white, colors['primary-gray-30']] : [colors['notification-new'], colors['primary-violet-100']];
  }, [isSelected, user_readed]);

  const date = notificationDateFormatter(createdAt);

  return (
    <Flex
      p="24px"
      align="center"
      pos="relative"
      boxShadow="0px 2px 5px 0px rgba(37, 85, 255, 0.05)"
      borderRadius="6px"
      w="100%"
      border={`1px solid ${border}`}
      bg={bg}
    >
      <Box
        p="0 12px"
        mr="8px"
        cursor="pointer"
        onClick={() => toggleSelect(id, isSelected)}
      >
        {isSelected ? FilledCheckBoxIcon() : CheckBoxIcon()}
      </Box>

      <Box mr="24px">
        <OneColorIcon
          color="linear-gradient(180deg, #B86BF8 0%, #7F5DD9 100%)"
          img="/images/notifications/admin/book.svg"
        />
      </Box>

      <Flex
        gap="12px"
        direction="column"
        width="100%"
        cursor="pointer"
        onClick={() => handleOpenNotification(id)}
      >
        <Flex
          justify="space-between"
          align="center"
        >
          <Heading
            size="poppins24"
            variant="primary-black-text"
            lineHeight="38px"
          >
            {t('Book Review Request')}
          </Heading>

          <Text
            variant="primary-black-text"
            size="poppins16normal"
            lineHeight="28px"
            letterSpacing="-0.01em"
          >
            {date}
          </Text>
        </Flex>

        <Text
          variant="primary-black-text"
          size="poppins16normal"
          lineHeight="28px"
          letterSpacing="-0.01em"
          maxW="670px"
          display='-webkit-box'
          sx={{
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          } as CSSProperties}
          overflow="hidden"
          textOverflow="ellipsis"
          wordBreak="break-word"
        >
          {t('For ')}
          <Text
            as="span"
            variant="primary-black-text"
            size="poppins16normal"
            fontWeight={600}
            lineHeight="28px"
            letterSpacing="-0.01em"
          >
            {title}
          </Text>
          <br />
          {t('by ')}
          <Text
            as="span"
            variant="primary-black-text"
            size="poppins16normal"
            fontWeight={600}
            lineHeight="28px"
            letterSpacing="-0.01em"
          >
          {createdBy}
          </Text>
        </Text>
      </Flex>
    </Flex>
  );
};

export const NewBookReviewRequestFull: FC<AdminFullProps> = ({
  book_id,
  title,
  createdAt,
  createdBy,
  createdByEmail,
}) => {
  const { t } = useTranslation();
  const date = notificationDateFormatter(createdAt);
  const navigate = useNavigate();

  const handleOpenRequest = useCallback(() => {
    navigate(`/admin/requests?active=${REQUESTS_TABS.STORIES}&query=${title}&open=${book_id}`);
  }, [title, book_id]);

  return (
    <Flex
      p="24px 24px 44px"
      align="center"
      pos="relative"
      boxShadow="0px 2px 5px 0px rgba(37, 85, 255, 0.05)"
      bg={colors.white}
      borderRadius="6px"
      w="100%"
      border={`1px solid ${colors['primary-gray-30']}`}
      gap="24px"
      direction="column"
      minH="calc(100vh - 90px - 44px)"
    >
      <Flex
        justify="space-between"
        pb="24px"
        borderBottom={`1px solid ${colors['primary-gray-30']}`}
        w="100%"
        align="center"
      >
        <Flex gap="24px" align="center">
          <Box>
            <OneColorIcon
              color="linear-gradient(180deg, #B86BF8 0%, #7F5DD9 100%)"
              img="/images/notifications/admin/book.svg"
            />
          </Box>

          <Text
            variant="primary-black-text"
            size="poppins16normal"
            lineHeight="28px"
            letterSpacing="-0.01em"
          >
            <Text
              as="span"
              variant="primary-black-text"
              size="poppins16normal"
              fontWeight={800}
              lineHeight="28px"
              letterSpacing="-0.01em"
            >
              {createdBy}
            </Text>
            {` <${createdByEmail}>`}
          </Text>
        </Flex>

        <Text
          variant="primary-black-text"
          size="poppins16normal"
          lineHeight="28px"
          letterSpacing="-0.01em"
        >
          {date}
        </Text>
      </Flex>

      <Image
        w="80px"
        h="80px"
        src="/images/contract/notebook.png"
        loading="eager"
        alt="sended for review"
      />

      <Text
        variant="primary-black-text"
        size="poppins24"
        lineHeight="22px"
        letterSpacing="-0.01em"
        fontWeight={600}
      >
        {t('Book Review Request')}
      </Text>

      <Text
        variant="primary-black-text"
        size="poppins24"
        lineHeight="36px"
        letterSpacing={0}
        fontWeight={400}
        textAlign="center"
      >
        {t('For ')}
        <Text
          as="span"
          variant="primary-black-text"
          size="poppins24"
          lineHeight="36px"
          letterSpacing={0}
          fontWeight={700}
        >
          {title}
        </Text>
        <br />
        {t('by ')}
        <Text
          as="span"
          variant="primary-black-text"
          size="poppins24"
          lineHeight="36px"
          letterSpacing={0}
          fontWeight={700}
        >
          {createdBy}
        </Text>
      </Text>

      <Text
        variant="primary-black-text"
        size="poppins20normal"
        lineHeight="32px"
        maxW="580px"
        mb="20px"
        textAlign="center"
      >
        {t('The contract has been successfully verified, and the book is now ready for review.')}
      </Text>

      <Button
        variant="defaultButton"
        onClick={handleOpenRequest}
      >
        {t('Go to review')}
      </Button>
    </Flex>
  );
};